import { useRef, useState } from "react";
import { useT } from "../i18n";
import { decode, MAX_CHAPTER_CHARS } from "./textfile";

/** What the picker offers and what a drop will accept. */
const ACCEPT = ".txt,.md,.markdown,.text,text/plain,text/markdown";
/** An EPUB is offered only where something can take one. */
const ACCEPT_EPUB = ".epub,application/epub+zip";

/**
 * TextFileDrop takes a .txt or .md file, by picker or by drop, and hands back
 * its text.
 *
 * It reads the file in the browser and fills the same field a paste would, so
 * there is no upload endpoint and no second path into the chapter text — the
 * file is a way of typing, not a way of importing.
 *
 * An .epub is the exception, and is handed over whole to `onEPUB`: it is a
 * book, not a chapter, and the server reads it (ADR-0052).
 */
export default function TextFileDrop({
  onText,
  onEPUB,
  disabled = false,
}: {
  onText: (text: string, meta: { filename: string; encoding: string }) => void;
  onEPUB?: (file: File) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  const [note, setNote] = useState<{ kind: "ok" | "warn"; text: string } | null>(null);

  async function take(file: File | undefined) {
    if (!file) return;
    setNote(null);

    // By name, because a drop's MIME type is often empty. The server checks
    // the bytes, and has its own size limit for a file full of pictures.
    if (onEPUB && /\.epub$/i.test(file.name)) {
      onEPUB(file);
      return;
    }

    // 16 MiB of text is far past the chapter limit and is almost always a
    // wrong file — a PDF or an epub renamed. Refuse before decoding it.
    if (file.size > 16 << 20) {
      setNote({ kind: "warn", text: t.file.tooLarge });
      return;
    }

    const { text, encoding } = decode(new Uint8Array(await file.arrayBuffer()));
    // Runes, not bytes: Persian is about two bytes a character, so a byte count
    // would report double the length and refuse chapters that are fine.
    const chars = [...text].length;

    if (chars === 0) {
      setNote({ kind: "warn", text: t.file.noText });
      return;
    }
    if (chars > MAX_CHAPTER_CHARS) {
      setNote({ kind: "warn", text: t.file.tooLong(chars, MAX_CHAPTER_CHARS) });
      return;
    }

    onText(text, { filename: file.name, encoding });
    setNote({
      kind: "ok",
      text:
        encoding === "UTF-8"
          ? t.file.loaded(file.name, chars)
          : t.file.loadedAs(file.name, encoding, chars),
    });
  }

  return (
    <div
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        setOver(false);
        void take(e.dataTransfer.files[0]);
      }}
      data-testid="text-file-drop"
      className={`rounded-control border border-dashed px-3 py-2.5 transition-colors ${
        over ? "border-sage bg-sage-soft" : "border-line-strong"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <button
          type="button"
          disabled={disabled}
          data-testid="choose-file"
          onClick={() => input.current?.click()}
          className="rounded-control border border-line px-2.5 py-1 text-xs font-medium transition-colors hover:bg-sunk disabled:opacity-40"
        >
          {t.file.choose}
        </button>
        {/* The hidden input is the real control; the button above just opens it,
            because a bare file input cannot be styled to match anything. */}
        <input
          ref={input}
          type="file"
          accept={onEPUB ? `${ACCEPT},${ACCEPT_EPUB}` : ACCEPT}
          disabled={disabled}
          data-testid="file-input"
          className="sr-only"
          onChange={(e) => {
            void take(e.target.files?.[0]);
            // Cleared so that choosing the same file twice fires change again —
            // otherwise a re-pick after an error looks like nothing happened.
            e.target.value = "";
          }}
        />
        <span className="text-xs text-muted">{t.file.orDrop}</span>
      </div>

      {note && (
        <p
          data-testid={note.kind === "ok" ? "file-loaded" : "file-error"}
          role={note.kind === "warn" ? "alert" : undefined}
          className={`mt-1.5 text-xs ${
            note.kind === "ok" ? "text-muted" : "text-red-600 dark:text-red-400"
          }`}
        >
          {note.text}
        </p>
      )}
    </div>
  );
}
